import { request, requestBlob, unwrap } from '../client';
import type { ApiResponse, BundleReport, ThemeResponse } from '../types';

const BASE = '/api/saas-admin/brands';

/** §3.2 — a compressed zip over this is `413 BUNDLE_TOO_LARGE` (checked client-side first). */
export const BUNDLE_MAX_BYTES = 10 * 1024 * 1024;

/**
 * §3.1's `brand-<slug>-<yyyyMMdd-HHmm>.zip` in UTC, built client-side: the
 * admin API is cross-origin and doesn't expose `Content-Disposition`.
 */
export function bundleFileName(slug: string, now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}`;
  const time = `${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}`;
  return `brand-${slug}-${date}-${time}.zip`;
}

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
const list = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const obj = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' ? (v as Record<string, unknown>) : {};

/**
 * §3.3 says `changes` is empty after a container error but doesn't pin its
 * wire shape (`{}`, null, sections without arrays), so every list the dialog
 * reads is defaulted to empty here; contrast defaults to ok with no failures.
 */
export function normalizeReport(raw: unknown): BundleReport {
  const r = obj(raw);
  const changes = obj(r.changes);
  const theme = obj(changes.theme);
  const copy = obj(changes.copy);
  const assets = obj(changes.assets);
  const contrast = obj(r.contrast);
  const locales: Record<string, string[]> = {};
  for (const [locale, keys] of Object.entries(obj(copy.locales))) locales[locale] = strings(keys);
  return {
    ok: r.ok === true,
    etagMatches: typeof r.etagMatches === 'boolean' ? r.etagMatches : null,
    changes: {
      theme: { changed: strings(theme.changed) },
      copy: { default: strings(copy.default), locales },
      assets: {
        added: strings(assets.added),
        replaced: strings(assets.replaced),
        cleared: strings(assets.cleared),
        unchanged: strings(assets.unchanged),
      },
    },
    contrast: {
      ok: contrast.ok !== false,
      failures: list<BundleReport['contrast']['failures'][number]>(contrast.failures),
    },
    warnings: list<BundleReport['warnings'][number]>(r.warnings),
    errors: list<BundleReport['errors'][number]>(r.errors),
  };
}

function bundleForm(file: File): FormData {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
}

/**
 * SASADM-0159 — the brand theme bundle (saas-auth SASATH-0351 §3, operator
 * base `A`). Dry runs and applies share one 10/min budget; export is 30/min.
 */
export const brandBundleApi = {
  /** §3.1 — the zip. `Content-Disposition` isn't exposed cross-origin; the caller names the file. */
  async exportBundle(brandId: number): Promise<Blob> {
    return requestBlob('GET', `${BASE}/${brandId}/theme/bundle`);
  },

  /** §3.2 — always 200 with the §3.3 report (errors → `ok: false`), except 413/401/403/404/429. */
  async dryRun(brandId: number, file: File): Promise<BundleReport> {
    const res = await request<ApiResponse<unknown>>('POST', `${BASE}/${brandId}/theme/bundle`, {
      query: { dryRun: 'true' },
      formData: bundleForm(file),
    });
    return normalizeReport(unwrap(res));
  },

  /**
   * §3.2 — precedence 400 `BUNDLE_INVALID` (`error.errors[]`), 409 `THEME_CHANGED`,
   * 422 `THEME_CONTRAST_FAILED` (`error.failures[]`); 200 is the `GET /theme` body.
   */
  async apply(brandId: number, file: File): Promise<ThemeResponse> {
    const res = await request<ApiResponse<ThemeResponse>>(
      'POST',
      `${BASE}/${brandId}/theme/bundle`,
      { query: { dryRun: 'false' }, formData: bundleForm(file) },
    );
    return unwrap(res);
  },
};
